import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import {
  Smartphone, MapPin, Navigation, Truck, User, CheckCircle, Clock,
  AlertTriangle, Fuel, Camera, Phone, MessageSquare, RefreshCw,
  Play, Pause, Square, ChevronRight, MapIcon, Package,
  Shield, Battery, Wifi, Signal, Calendar, Route, QrCode,
  FileText, Upload, Download, Settings, Bell, X, Check } from
'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { useFleetAPI, useDrivers, useVehicles } from '../hooks/useFleetData';

const API_URL = API_BASE;

// Trip status colors
const tripStatusColors = {
  PLANNED: 'bg-blue-100 text-blue-700',
  ASSIGNED: 'bg-purple-100 text-purple-700',
  IN_PROGRESS: 'bg-amber-100 text-amber-700',
  COMPLETED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-gray-100 text-gray-700'
};

const DriverMobileAppPage = () => {const { t } = useTranslation("fleet");
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverTrips, setDriverTrips] = useState([]);
  const [activeTrip, setActiveTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locationEnabled, setLocationEnabled] = useState(true);
  const [showTripDetail, setShowTripDetail] = useState(false);
  const [selectedTripDetail, setSelectedTripDetail] = useState(null);

  const { drivers } = useDrivers({});
  const { vehicles } = useVehicles({});
  const { fetchAPI } = useFleetAPI();

  useEffect(() => {
    loadDriverData();
  }, []);

  const loadDriverData = async () => {
    setLoading(true);
    try {
      // For demo, use first driver or mock data
      if (drivers.length > 0) {
        setSelectedDriver(drivers[0]);
      } else {
        setSelectedDriver({
          id: 1,
          driver_code: 'DRV-001',
          first_name: 'Carlos',
          last_name: 'Silva',
          phone: '+55 11 99999-9999',
          status: 'AVAILABLE',
          rating: 4.8,
          total_trips: 156,
          total_distance_km: 12450,
          current_vehicle_id: 1
        });
      }

      // Load trips
      const tripsData = await fetchAPI('/trips?limit=20');
      setDriverTrips(tripsData || []);

      // Find active trip
      const active = (tripsData || []).find((t) =>
      (t.status || '').toUpperCase() === 'IN_PROGRESS'
      );
      setActiveTrip(active);
    } catch (err) {
      console.error('Failed to load driver data:', err);
      // Mock data for demo
      setDriverTrips([
      {
        id: 1,
        trip_code: 'TRP-260204-001',
        status: 'IN_PROGRESS',
        origin_name: 'São Paulo Warehouse',
        origin_address: 'Rua da Consolação, 1000, São Paulo',
        destination_name: 'Customer A',
        destination_address: 'Av. Paulista, 2000, São Paulo',
        scheduled_start: '2026-02-04T08:00:00Z',
        actual_start: '2026-02-04T08:15:00Z',
        planned_distance_km: 15.5,
        cargo_description: '10 boxes - Electronics',
        stops: [
        { id: 1, sequence: 1, name: 'Stop 1', address: 'Address 1', status: 'completed' },
        { id: 2, sequence: 2, name: 'Stop 2', address: 'Address 2', status: 'pending' },
        { id: 3, sequence: 3, name: 'Stop 3', address: 'Address 3', status: 'pending' }]

      },
      {
        id: 2,
        trip_code: 'TRP-260204-002',
        status: 'PLANNED',
        origin_name: 'Distribution Center',
        origin_address: 'Rua Augusta, 500, São Paulo',
        destination_name: 'Customer B',
        destination_address: 'Rua Oscar Freire, 1200, São Paulo',
        scheduled_start: '2026-02-04T14:00:00Z',
        planned_distance_km: 8.2,
        cargo_description: '5 boxes - Clothing'
      },
      {
        id: 3,
        trip_code: 'TRP-260203-001',
        status: 'COMPLETED',
        origin_name: 'Main Warehouse',
        origin_address: 'Av. Brasil, 1000',
        destination_name: 'Customer C',
        destination_address: 'Rua Haddock Lobo, 800',
        scheduled_start: '2026-02-03T09:00:00Z',
        actual_start: '2026-02-03T09:10:00Z',
        actual_end: '2026-02-03T11:30:00Z',
        planned_distance_km: 12.0,
        actual_distance_km: 13.2,
        cargo_description: '8 boxes - Documents'
      }]
      );
      setActiveTrip({
        id: 1,
        trip_code: 'TRP-260204-001',
        status: 'IN_PROGRESS'
      });
    } finally {
      setLoading(false);
    }
  };

  // Start/end trip actions
  const startTrip = async (tripId) => {
    try {
      await fetchAPI(`/trips/${tripId}/start`, { method: 'PUT' });
      loadDriverData();
    } catch (err) {
      console.error('Failed to start trip:', err);
      // Update locally for demo
      setDriverTrips((prev) => prev.map((t) =>
      t.id === tripId ? { ...t, status: 'IN_PROGRESS', actual_start: new Date().toISOString() } : t
      ));
      setActiveTrip({ id: tripId, trip_code: `TRP-${tripId}`, status: 'IN_PROGRESS' });
    }
  };

  const completeTrip = async (tripId) => {
    try {
      await fetchAPI(`/trips/${tripId}/complete`, { method: 'PUT' });
      loadDriverData();
    } catch (err) {
      console.error('Failed to complete trip:', err);
      // Update locally for demo
      setDriverTrips((prev) => prev.map((t) =>
      t.id === tripId ? { ...t, status: 'COMPLETED', actual_end: new Date().toISOString() } : t
      ));
      setActiveTrip(null);
    }
  };

  const completeStop = async (tripId, stopId) => {
    try {
      await fetchAPI(`/trips/${tripId}/stops/${stopId}/complete`, { method: 'PUT' });
      loadDriverData();
    } catch (err) {
      // Update locally for demo
      setDriverTrips((prev) => prev.map((t) => {
        if (t.id === tripId && t.stops) {
          return {
            ...t,
            stops: t.stops.map((s) => s.id === stopId ? { ...s, status: 'completed' } : s)
          };
        }
        return t;
      }));
    }
  };

  // Calculate stats
  const stats = {
    todayTrips: driverTrips.filter((t) => {
      const tripDate = new Date(t.scheduled_start || t.created_at);
      const today = new Date();
      return tripDate.toDateString() === today.toDateString();
    }).length,
    completed: driverTrips.filter((t) => (t.status || '').toUpperCase() === 'COMPLETED').length,
    pending: driverTrips.filter((t) => ['PLANNED', 'ASSIGNED'].includes((t.status || '').toUpperCase())).length,
    inProgress: driverTrips.filter((t) => (t.status || '').toUpperCase() === 'IN_PROGRESS').length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
      </div>);

  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile App Header */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Smartphone className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-bold">{t("driverPortal", "Driver Portal")}</h1>
              <p className="text-xs text-white/80">{t("fleetManagementMobile", "Fleet Management Mobile")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
            locationEnabled ? 'bg-green-500/30' : 'bg-yellow-500/30'}`
            }>
              {locationEnabled ? <Wifi className="w-3 h-3" /> : <Signal className="w-3 h-3" />}
              {locationEnabled ? 'GPS Active' : 'GPS Off'}
            </div>
            <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 p-2">
              <Bell className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Driver Info Card */}
      {selectedDriver &&
      <div className="p-4 -mt-2">
          <Card className="bg-white border-0 shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center text-white text-xl font-bold">
                  {selectedDriver.first_name?.[0]}{selectedDriver.last_name?.[0]}
                </div>
                <div className="flex-1">
                  <h2 className="font-bold text-gray-900 text-lg">
                    {selectedDriver.first_name} {selectedDriver.last_name}
                  </h2>
                  <p className="text-sm text-gray-500">{selectedDriver.driver_code}</p>
                  <div className="flex items-center gap-3 mt-1">
                    <Badge className={`${
                  selectedDriver.status === 'ON_DUTY' ? 'bg-green-100 text-green-700' :
                  selectedDriver.status === 'AVAILABLE' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'}`
                  }>
                      {selectedDriver.status}
                    </Badge>
                    <div className="flex items-center text-amber-500">
                      <span className="text-sm font-medium">★ {selectedDriver.rating || 4.8}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t">
                <div className="text-center">
                  <p className="text-xl font-bold text-gray-900">{selectedDriver.total_trips || 156}</p>
                  <p className="text-xs text-gray-500">{t("totalTrips", "Total Trips")}</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-gray-900">{((selectedDriver.total_distance_km || 12450) / 1000).toFixed(1)}k</p>
                  <p className="text-xs text-gray-500">{t("kmDriven", "KM Driven")}</p>
                </div>
                <div className="text-center">
                  <p className="text-xl font-bold text-green-600">{stats.completed}</p>
                  <p className="text-xs text-gray-500">{t("thisMonth", "This Month")}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      }

      {/* Active Trip Alert */}
      {activeTrip &&
      <div className="px-4 mb-4">
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 border-2">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center animate-pulse">
                    <Truck className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-amber-800">{t("activeTrip", "Active Trip")}</p>
                    <p className="text-xs text-amber-600">{activeTrip.trip_code}</p>
                  </div>
                </div>
                <Badge className="bg-amber-500 text-white">{t("inProgress", "IN PROGRESS")}</Badge>
              </div>
              <div className="flex gap-2">
                <Button
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                onClick={() => {
                  const trip = driverTrips.find((t) => t.id === activeTrip.id);
                  setSelectedTripDetail(trip);
                  setShowTripDetail(true);
                }}>
                
                  <MapPin className="w-4 h-4 mr-2" />{t("viewRoute", "View Route")}

              </Button>
                <Button
                variant="outline"
                className="flex-1 border-amber-500 text-amber-700 hover:bg-amber-100"
                onClick={() => completeTrip(activeTrip.id)}>
                
                  <CheckCircle className="w-4 h-4 mr-2" />{t("complete", "Complete")}

              </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      }

      {/* Tabs Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="px-4">
        <TabsList className="bg-white shadow-sm w-full grid grid-cols-4 h-12">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <LayoutDashboard className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{t("today", "Today")}</span>
          </TabsTrigger>
          <TabsTrigger value="trips" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <Route className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{t("trips", "Trips")}</span>
          </TabsTrigger>
          <TabsTrigger value="vehicle" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <Truck className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{t("vehicle", "Vehicle")}</span>
          </TabsTrigger>
          <TabsTrigger value="profile" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <User className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{t("profile", "Profile")}</span>
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab - Today's Overview */}
        <TabsContent value="dashboard" className="mt-4 space-y-4">
          {/* Today's Stats */}
          <div className="grid grid-cols-2 gap-3">
            <Card className="bg-white border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{stats.todayTrips}</p>
                    <p className="text-xs text-gray-500">{t("todaysTrips", "Today's Trips")}</p>
                  </div>
                  <Calendar className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
                    <p className="text-xs text-gray-500">{t("pending", "Pending")}</p>
                  </div>
                  <Clock className="w-8 h-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Today's Trips List */}
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">{t("todaysSchedule", "Today's Schedule")}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {driverTrips.length === 0 ?
              <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>{t("noTripsScheduled", "No trips scheduled for today")}</p>
                </div> :

              <div className="divide-y">
                  {driverTrips.slice(0, 5).map((trip) => {
                  const status = (trip.status || '').toUpperCase();
                  return (
                    <div
                      key={trip.id}
                      className="p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        setSelectedTripDetail(trip);
                        setShowTripDetail(true);
                      }}>
                      
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">{trip.trip_code}</span>
                          <Badge className={tripStatusColors[status] || 'bg-gray-100 text-gray-700'}>
                            {status.replace('_', ' ')}
                          </Badge>
                        </div>
                        <div className="flex items-start gap-2 text-sm text-gray-500">
                          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-gray-700">{trip.destination_name}</p>
                            <p className="text-xs truncate">{trip.destination_address}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {trip.scheduled_start ? new Date(trip.scheduled_start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'TBD'}
                          </span>
                          <span>{trip.planned_distance_km}{t("km", "km")}</span>
                        </div>
                      </div>);

                })}
                </div>
              }
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <div className="grid grid-cols-3 gap-3">
            <Button variant="outline" className="h-20 flex flex-col items-center justify-center gap-2">
              <Camera className="w-6 h-6 text-gray-600" />
              <span className="text-xs">{t("photoPod", "Photo POD")}</span>
            </Button>
            <Button variant="outline" className="h-20 flex flex-col items-center justify-center gap-2">
              <Fuel className="w-6 h-6 text-gray-600" />
              <span className="text-xs">{t("logFuel", "Log Fuel")}</span>
            </Button>
            <Button variant="outline" className="h-20 flex flex-col items-center justify-center gap-2">
              <AlertTriangle className="w-6 h-6 text-gray-600" />
              <span className="text-xs">{t("reportIssue", "Report Issue")}</span>
            </Button>
          </div>
        </TabsContent>

        {/* Trips Tab */}
        <TabsContent value="trips" className="mt-4 space-y-4">
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-500">{t("allTrips", "All Trips")}</CardTitle>
                <Button variant="ghost" size="sm" onClick={loadDriverData}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y max-h-[60vh] overflow-auto">
                {driverTrips.map((trip) => {
                  const status = (trip.status || '').toUpperCase();
                  return (
                    <div
                      key={trip.id}
                      className="p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => {
                        setSelectedTripDetail(trip);
                        setShowTripDetail(true);
                      }}>
                      
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{trip.trip_code}</span>
                        <Badge className={tripStatusColors[status] || 'bg-gray-100 text-gray-700'}>
                          {status.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2 text-gray-500">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="truncate">{trip.origin_name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-500">
                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                          <span className="truncate">{trip.destination_name}</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        {status === 'PLANNED' || status === 'ASSIGNED' ?
                        <Button
                          size="sm"
                          className="bg-green-500 hover:bg-green-600 text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            startTrip(trip.id);
                          }}>
                          
                            <Play className="w-3 h-3 mr-1" />{t("startTrip", "Start Trip")}

                        </Button> :
                        status === 'IN_PROGRESS' ?
                        <Button
                          size="sm"
                          className="bg-amber-500 hover:bg-amber-600 text-white"
                          onClick={(e) => {
                            e.stopPropagation();
                            completeTrip(trip.id);
                          }}>
                          
                            <CheckCircle className="w-3 h-3 mr-1" />{t("complete", "Complete")}

                        </Button> :

                        <span className="text-xs text-gray-400">
                            {trip.actual_end ? new Date(trip.actual_end).toLocaleDateString() : ''}
                          </span>
                        }
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>);

                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vehicle Tab */}
        <TabsContent value="vehicle" className="mt-4 space-y-4">
          <Card className="bg-white border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-red-500" />{t("assignedVehicle", "Assigned Vehicle")}

              </CardTitle>
            </CardHeader>
            <CardContent>
              {vehicles.length > 0 ?
              <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Truck className="w-10 h-10 text-gray-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">{vehicles[0].vehicle_code}</h3>
                      <p className="text-sm text-gray-500">{vehicles[0].make} {vehicles[0].model}</p>
                      <p className="text-sm text-gray-400">{t("plate", "Plate:")}{vehicles[0].license_plate}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 pt-4 border-t">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">{t("odometer", "Odometer")}</p>
                      <p className="font-bold">{(vehicles[0].odometer_km || 45000).toLocaleString()}{t("km", "km")}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">{t("fuelLevel", "Fuel Level")}</p>
                      <p className="font-bold">{vehicles[0].current_fuel_level || 75}%</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">{t("nextService", "Next Service")}</p>
                      <p className="font-bold text-amber-600">{t("in2500Km", "In 2,500 km")}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">{t("status", "Status")}</p>
                      <p className="font-bold text-green-600">{t("active", "Active")}</p>
                    </div>
                  </div>
                </div> :

              <div className="text-center py-8 text-gray-500">
                  <Truck className="w-16 h-16 mx-auto mb-2 opacity-30" />
                  <p className="font-medium">{t("noVehicleAssigned", "No Vehicle Assigned")}</p>
                  <p className="text-sm">{t("contactYourFleet", "Contact your fleet manager")}</p>
                </div>
              }
            </CardContent>
          </Card>

          {/* Vehicle Actions */}
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" className="h-16 flex flex-col items-center justify-center">
              <Fuel className="w-5 h-5 mb-1" />
              <span className="text-xs">{t("logFuel", "Log Fuel")}</span>
            </Button>
            <Button variant="outline" className="h-16 flex flex-col items-center justify-center">
              <AlertTriangle className="w-5 h-5 mb-1" />
              <span className="text-xs">{t("reportIssue", "Report Issue")}</span>
            </Button>
            <Button variant="outline" className="h-16 flex flex-col items-center justify-center">
              <Camera className="w-5 h-5 mb-1" />
              <span className="text-xs">{t("takePhoto", "Take Photo")}</span>
            </Button>
            <Button variant="outline" className="h-16 flex flex-col items-center justify-center">
              <FileText className="w-5 h-5 mb-1" />
              <span className="text-xs">{t("documents", "Documents")}</span>
            </Button>
          </div>
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-4 space-y-4">
          {selectedDriver &&
          <>
              <Card className="bg-white border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <div className="w-24 h-24 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center text-white text-3xl font-bold mx-auto mb-3">
                      {selectedDriver.first_name?.[0]}{selectedDriver.last_name?.[0]}
                    </div>
                    <h2 className="font-bold text-xl text-gray-900">
                      {selectedDriver.first_name} {selectedDriver.last_name}
                    </h2>
                    <p className="text-gray-500">{selectedDriver.driver_code}</p>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-gray-500">{t("phone", "Phone")}</span>
                      <span className="font-medium">{selectedDriver.phone || '+55 11 99999-9999'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-gray-500">{t("licenseClass", "License Class")}</span>
                      <span className="font-medium">{selectedDriver.license_class || 'C'}</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-gray-500">{t("rating", "Rating")}</span>
                      <span className="font-medium text-amber-500">★ {selectedDriver.rating || 4.8}</span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-gray-500">{t("status", "Status")}</span>
                      <Badge className={selectedDriver.status === 'AVAILABLE' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                        {selectedDriver.status || 'AVAILABLE'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Profile Actions */}
              <div className="space-y-2">
                <Button variant="outline" className="w-full justify-start h-12">
                  <Settings className="w-5 h-5 mr-3" />{t("settings", "Settings")}

              </Button>
                <Button variant="outline" className="w-full justify-start h-12">
                  <FileText className="w-5 h-5 mr-3" />{t("myDocuments", "My Documents")}

              </Button>
                <Button variant="outline" className="w-full justify-start h-12">
                  <Shield className="w-5 h-5 mr-3" />{t("safetyTraining", "Safety Training")}

              </Button>
                <Button variant="outline" className="w-full justify-start h-12 text-red-600 border-red-200 hover:bg-red-50">
                  <Phone className="w-5 h-5 mr-3" />{t("emergencyContact", "Emergency Contact")}

              </Button>
              </div>
            </>
          }
        </TabsContent>
      </Tabs>

      {/* Trip Detail Modal */}
      {showTripDetail && selectedTripDetail &&
      <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <Card className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-auto">
            <CardHeader className="border-b sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{selectedTripDetail.trip_code}</CardTitle>
                  <Badge className={tripStatusColors[(selectedTripDetail.status || '').toUpperCase()]}>
                    {(selectedTripDetail.status || '').toUpperCase().replace('_', ' ')}
                  </Badge>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setShowTripDetail(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {/* Route */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">{t("route", "Route")}</h4>
                <div className="relative pl-6 space-y-4">
                  <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-gray-200"></div>
                  
                  {/* Origin */}
                  <div className="relative">
                    <div className="absolute -left-4 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                    <div>
                      <p className="font-medium text-gray-900">{selectedTripDetail.origin_name}</p>
                      <p className="text-sm text-gray-500">{selectedTripDetail.origin_address}</p>
                    </div>
                  </div>
                  
                  {/* Stops */}
                  {selectedTripDetail.stops?.map((stop, idx) =>
                <div key={stop.id} className="relative">
                      <div className={`absolute -left-4 w-4 h-4 rounded-full border-2 border-white ${
                  stop.status === 'completed' ? 'bg-green-500' : 'bg-gray-300'}`
                  }></div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{stop.name}</p>
                          <p className="text-sm text-gray-500">{stop.address}</p>
                        </div>
                        {stop.status !== 'completed' &&
                    <Button
                      size="sm"
                      className="bg-green-500 hover:bg-green-600 text-white"
                      onClick={() => completeStop(selectedTripDetail.id, stop.id)}>
                      
                            <Check className="w-3 h-3" />
                          </Button>
                    }
                      </div>
                    </div>
                )}
                  
                  {/* Destination */}
                  <div className="relative">
                    <div className="absolute -left-4 w-4 h-4 bg-red-500 rounded-full border-2 border-white"></div>
                    <div>
                      <p className="font-medium text-gray-900">{selectedTripDetail.destination_name}</p>
                      <p className="text-sm text-gray-500">{selectedTripDetail.destination_address}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cargo Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">{t("cargo", "Cargo")}</h4>
                <p className="text-sm text-gray-600">{selectedTripDetail.cargo_description || 'No cargo description'}</p>
                <div className="flex items-center gap-4 mt-3 text-sm">
                  <span className="text-gray-500">{t("distance", "Distance:")}<strong>{selectedTripDetail.planned_distance_km}{t("km", "km")}</strong></span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                {(selectedTripDetail.status || '').toUpperCase() === 'IN_PROGRESS' ?
              <Button
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                onClick={() => {
                  completeTrip(selectedTripDetail.id);
                  setShowTripDetail(false);
                }}>
                
                    <CheckCircle className="w-4 h-4 mr-2" />{t("completeTrip", "Complete Trip")}

              </Button> :
              (selectedTripDetail.status || '').toUpperCase() === 'PLANNED' ?
              <Button
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                onClick={() => {
                  startTrip(selectedTripDetail.id);
                  setShowTripDetail(false);
                }}>
                
                    <Play className="w-4 h-4 mr-2" />{t("startTrip", "Start Trip")}

              </Button> :
              null}
                <Button variant="outline" className="flex-1">
                  <Navigation className="w-4 h-4 mr-2" />{t("navigate", "Navigate")}

              </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      }
    </div>);

};

// Import icons that aren't imported at top
const LayoutDashboard = ({ className }) =>
<svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="7" height="9" x="3" y="3" rx="1" />
    <rect width="7" height="5" x="14" y="3" rx="1" />
    <rect width="7" height="9" x="14" y="12" rx="1" />
    <rect width="7" height="5" x="3" y="16" rx="1" />
  </svg>;


export default DriverMobileAppPage;